import { useReplicantSelector } from "@livcg/nodecg-react";
import type { MatchPhase, ProjectsReplicant } from "@livcg/schema";
import { Archive, CalendarClock, RefreshCw, Search } from "lucide-react";
import { useState } from "react";
import { sendRaw } from "../shared/sendRaw.js";
import { useFetch } from "../shared/useFetch.js";
import { Badge, Button, ConfirmDialog, cn, EmptyState, focusRing, useToast } from "../ui/index.js";
import { PHASE_LABEL } from "./phases.js";
import { groupByStatus, type Project } from "./projectList.js";

/** Live state shown on the card — the clock tick (minute/second) is not included,
 *  so the screen does not re-render every second. */
type LiveSummary = {
  home: string | null;
  away: string | null;
  homeColor: string | null;
  awayColor: string | null;
  homeScore: number;
  awayScore: number;
  phase: MatchPhase;
  running: boolean;
  onAir: string | null;
};

type Summaries = Record<string, LiveSummary>;

function summarize(record: ProjectsReplicant | undefined): Summaries {
  const out: Summaries = {};
  for (const [slug, p] of Object.entries(record ?? {})) {
    out[slug] = {
      home: p.teams.home?.shortName ?? p.teams.home?.name ?? null,
      away: p.teams.away?.shortName ?? p.teams.away?.name ?? null,
      homeColor: p.teams.home?.colorPrimary ?? null,
      awayColor: p.teams.away?.colorPrimary ?? null,
      homeScore: p.match.homeScore,
      awayScore: p.match.awayScore,
      phase: p.match.phase,
      running: p.match.running,
      onAir: p.overlay.mode ?? (p.varOverlay.visible ? "var" : null),
    };
  }
  return out;
}

const sameSummaries = (a: Summaries, b: Summaries) => JSON.stringify(a) === JSON.stringify(b);

function formatKickoff(kickoffAt: string | null): string | null {
  if (!kickoffAt) return null;
  const d = new Date(kickoffAt.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("uz-UZ", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type Props = {
  onSelect: (slug: string) => void;
};

/** The first screen after login: the operator picks the project DELIBERATELY —
 *  the control panel never opens by itself. */
export function ProjectPicker({ onSelect }: Props) {
  const projects = useFetch<Project[]>("/api/projects?status=active");
  const live = useReplicantSelector<ProjectsReplicant, Summaries>(
    "projects",
    summarize,
    sameSummaries,
  );
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [archiving, setArchiving] = useState<Project | null>(null);

  const archive = async (p: Project) => {
    try {
      // The extension resolves business errors as { ok:false, error }.
      const res = (await sendRaw("project.archive", { slug: p.slug })) as
        | { ok?: boolean; error?: string }
        | undefined;
      if (res && res.ok === false) {
        toast({
          tone: "error",
          title: "Arxivlab bo'lmadi",
          description: res.error ?? "archive_failed",
        });
        return;
      }
      projects.refetch();
      toast({ tone: "success", title: "Arxivlandi", description: `/${p.slug}` });
    } catch (e) {
      toast({
        tone: "error",
        title: "Arxivlab bo'lmadi",
        description: (e as Error).message || "transport_error",
      });
    }
  };

  const q = query.trim().toLowerCase();
  const list = (projects.data ?? []).filter(
    (p) => !q || p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q),
  );
  // Only nearby matches by default; a search looks through all of them.
  const { groups, hidden } = groupByStatus(list, (slug) => live[slug]?.phase, Date.now(), {
    all: showAll || q !== "",
  });

  return (
    <div className="flex-1 overflow-y-auto px-8 py-8">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[22px] font-semibold text-ink">Loyihani tanlang</h1>
            <p className="mt-1 text-[13px] text-ink-muted">
              Qaysi match efirini boshqarishingizni tanlang. Tanlov faqat shu brauzerga tegishli.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Loyiha yoki slug…"
                className="h-9 w-64 rounded-md border border-border bg-surface pl-8 pr-3 text-[13px] text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-strong/60"
              />
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={projects.refetch}
              title="Ro'yxatni yangilash"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", projects.loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {projects.error ? (
          <EmptyState
            title="Loyihalar ro'yxati yuklanmadi"
            hint={`Server xatosi: ${projects.error.message}`}
            action={
              <Button size="sm" onClick={projects.refetch}>
                Qayta urinish
              </Button>
            }
          />
        ) : projects.data === null ? (
          <div className="py-16 text-center text-ink-muted">Yuklanmoqda…</div>
        ) : list.length === 0 ? (
          <EmptyState
            title={projects.data.length ? "Mos loyiha topilmadi" : "Faol loyiha yo'q"}
            hint={projects.data.length ? undefined : "Admin panelida yangi loyiha yarating."}
          />
        ) : groups.length === 0 ? (
          <EmptyState
            title="Yaqin oraliqda o'yin yo'q"
            hint="Jonli o'yin ham, keyingi 14 kunda rejadagisi ham, oxirgi 7 kundagi natija ham yo'q."
          />
        ) : (
          groups.map((group) => (
            <section key={group.key} className="space-y-2">
              <h2 className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-faint">
                {group.label}
              </h2>
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.items.map((p) => (
                  <li key={p.id} className="group/card relative">
                    <ProjectCard project={p} live={live[p.slug]} onSelect={onSelect} />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      onClick={() => setArchiving(p)}
                      title={`/${p.slug} — arxivlash`}
                      aria-label={`/${p.slug} loyihasini arxivlash`}
                      className="absolute right-2 top-2 text-ink-faint opacity-0 transition hover:text-danger group-hover/card:opacity-100 focus-visible:opacity-100"
                    >
                      <Archive className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
        {projects.data !== null && (hidden > 0 || showAll) && !q && (
          <div className="flex items-center gap-3 text-[12px] text-ink-muted">
            {showAll
              ? "Barcha faol loyihalar ko'rsatilmoqda."
              : `Yana ${hidden} ta loyiha yaqin oraliqdan (−7 … +14 kun) tashqarida.`}
            <Button variant="ghost" size="xs" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "Faqat yaqinlar" : "Hammasini ko'rsatish"}
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={archiving !== null}
        title="Loyihani arxivlash"
        description={
          archiving
            ? `/${archiving.slug} loyihasini arxivlashni tasdiqlaysizmi?\nRo'yxatdan yo'qoladi; Admin › Arxiv'dan qaytarish mumkin.`
            : undefined
        }
        confirmLabel="Arxivlash"
        tone="danger"
        onConfirm={() => {
          const p = archiving;
          setArchiving(null);
          if (p) void archive(p);
        }}
        onCancel={() => setArchiving(null)}
      />
    </div>
  );
}

function ProjectCard({
  project,
  live,
  onSelect,
}: {
  project: Project;
  live: LiveSummary | undefined;
  onSelect: (slug: string) => void;
}) {
  const kickoff = formatKickoff(project.kickoffAt);
  return (
    <button
      type="button"
      onClick={() => onSelect(project.slug)}
      className={cn(
        focusRing,
        "flex h-full w-full flex-col gap-3 rounded-lg border border-border bg-surface p-4 text-left transition-colors",
        "hover:border-border-strong hover:bg-surface-elevated",
        live?.onAir && "border-danger/40",
      )}
    >
      <div className="flex items-center gap-2 pr-7">
        {live?.onAir ? (
          <Badge tone="live" dot pulse>
            Efirda
          </Badge>
        ) : live?.running ? (
          <Badge tone="success" dot pulse>
            Soat yuryapti
          </Badge>
        ) : null}
        {live && <span className="text-[11px] text-ink-muted">{PHASE_LABEL[live.phase]}</span>}
      </div>

      {live?.home && live.away ? (
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <TeamName name={live.home} color={live.homeColor} />
          <span className="font-mono text-[18px] font-semibold tabular-nums text-ink">
            {live.homeScore}–{live.awayScore}
          </span>
          <TeamName name={live.away} color={live.awayColor} align="right" />
        </div>
      ) : (
        <div className="text-[15px] font-semibold text-ink">{project.name}</div>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 text-[11px] text-ink-muted">
        <span className="truncate font-mono">/{project.slug}</span>
        {kickoff && (
          <span className="inline-flex shrink-0 items-center gap-1">
            <CalendarClock className="h-3 w-3" />
            {kickoff}
          </span>
        )}
      </div>
    </button>
  );
}

function TeamName({ name, color, align }: { name: string; color: string | null; align?: "right" }) {
  return (
    <span
      className={cn(
        "flex min-w-0 items-center gap-1.5",
        align === "right" && "flex-row-reverse text-right",
      )}
    >
      <span
        className="h-3 w-3 shrink-0 rounded-sm border border-border"
        style={color ? { backgroundColor: color } : undefined}
      />
      <span className="truncate text-[14px] font-medium text-ink">{name}</span>
    </span>
  );
}
